import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LogErrors } from './../../../../utilities/LogErrors'
import { ValidateSession } from './../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'

import { InstructorBase } from './../../../Layouts/Instructor/V2/InstructorBase'
import { Search } from './../../../Common/Tables/Search'
import { Loading } from './../../../Common/Loading'
import { HELP_TYPE } from './../../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../utilities/EL/Roles'
import { ATTEMPT_SCORE_FILTER, ATTEMPT_SCORE_FILTER_LABELS } from './../../../../utilities/EL/AttemptScoreFilter'

// Excel utils
import { BuildAndDownloadWorkbook, FormatDateTime, BuildSheetWithMeta } from './../../../../utilities/BL/Instructor/ExcelExporter'

// Importaciones de la Capa de Negocios (Líder)
import { GetInstructorsList, GetCoursesForSelectedGroup, GetEvaluationsForSelectedCourse, GetAllResultsForInstructor, GetFilteredTableResults, GetDetailedSingleReport, GetQuestionDetail as BLGetQuestionDetail } from './../../../../utilities/BL/Leader/Results'


const Reports = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()
    const [isLoading, setIsLoading] = useState(true)

    // =========================================================================
    // 1. ESTADOS PARA FILTROS EN CASCADA (Instructor -> Grupo -> Curso -> Evaluación)
    // =========================================================================
    
    // --- Instructor ---
    const [instructorsList, setInstructorsList] = useState([])
    const [selectedInstructorId, setSelectedInstructorId] = useState(0)
    const [isInstructorsListLoading, setIsInstructorsListLoading] = useState(false)

    // --- Grupo --- (Se extraen localmente del Instructor seleccionado)
    const [groupsList, setGroupsList] = useState([])
    const [selectedGroupId, setSelectedGroupId] = useState(0)

    // --- Curso --- (Se solicitan a la API tras elegir el Grupo)
    const [coursesList, setCoursesList] = useState([])
    const [selectedCourseId, setSelectedCourseId] = useState(0)
    const [isCoursesListLoading, setIsCoursesListLoading] = useState(false)

    // --- Evaluación --- (Se solicitan a la API tras elegir el Curso)
    const [evaluationsList, setEvaluationsList] = useState([])
    const [selectedEvaluationId, setSelectedEvaluationId] = useState(0)
    const [isEvaluationsListLoading, setIsEvaluationsListLoading] = useState(false)

    // --- Tipo de resultados --- (no depende de la cascada, se envía tal cual al aplicar filtro)
    const [selectedAttemptScoreFilter, setSelectedAttemptScoreFilter] = useState(ATTEMPT_SCORE_FILTER.ALL)


    // =========================================================================
    // 2. ESTADOS DE TABLA Y BÚSQUEDA
    // =========================================================================
    const [allRows, setAllRows] = useState([]) // Filas crudas de la API
    const [filteredRows, setFilteredRows] = useState([]) // Filas mostradas en tabla (afectadas por buscador)
    const [searchText, setSearchText] = useState('')
    const allRowsRef = useRef([])

    useEffect(() => { allRowsRef.current = allRows }, [allRows])

    // Filtro local en tiempo real para el buscador de texto
    useEffect(() => {
        const text = (searchText ?? '').toLowerCase()
        if (!text) { setFilteredRows(allRows); return }

        const filtered = allRows.filter(r => {
            const fields = [
                `${r?.name ?? ''} ${r?.lastName ?? ''}`,
                r?.user,
                r?.attemptNumber,
                r?.blockName ?? r?.nameEvaluation ?? r?.type,
                r?.finishDate ? FormatDateTime(r.finishDate) : '',
                r?.score
            ]
            return fields.some(v => String(v ?? '').toLowerCase().includes(text))
        })
        setFilteredRows(filtered)
    }, [searchText, allRows])

    // --- Detalle pregunta por pregunta (página "Detalles", igual que /results/items) ---
    const [isFetchingDetail, setIsFetchingDetail] = useState(false)
    const [questionDetail, setQuestionDetail] = useState(null)
    const [showQuestionDetailPage, setShowQuestionDetailPage] = useState(false)
    const [activeDetailFilter, setActiveDetailFilter] = useState('all') // 'all' | 'correct' | 'incorrect'

    // El propio endpoint aclara que correctQuestions/incorrectQuestions cuentan TAREAS, no
    // preguntas (una pregunta de acreditación puede tener varias tareas). Se recalculan aquí
    // igual que en /results/items, para que coincidan exactamente con lo que se lista abajo.
    const isDetailAccreditation = useMemo(
        () => (questionDetail?.questions ?? []).some(q => q.tasks.length > 1),
        [questionDetail]
    )
    const totalDetailTasks = useMemo(
        () => (questionDetail?.questions ?? []).reduce((sum, q) => sum + q.tasks.length, 0),
        [questionDetail]
    )
    const correctDetailTasks = useMemo(
        () => (questionDetail?.questions ?? []).reduce((sum, q) => sum + q.tasks.filter(t => t.result === 1).length, 0),
        [questionDetail]
    )
    const incorrectDetailTasks = totalDetailTasks - correctDetailTasks


    // =========================================================================
    // 3. ESTADOS DE CONTROL Y PAGINACIÓN
    // =========================================================================
    const [isWorking, setIsWorking] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)

    const [pageNumber, setPageNumber] = useState(1)
    const [pageSize, setPageSize] = useState(100)
    const [hasMorePages, setHasMorePages] = useState(false)

    const [centerInfo, setCenterInfo] = useState({ idCenter: null, centerName: '' })


    // =========================================================================
    // 4. HELPERS DE APOYO (Exportación y Nombres)
    // =========================================================================
    const getEvaluationTypeName = (evaluationType, fallbackBlockName) => {
        switch (Number(evaluationType)) {
            case 1: return 'Diagnóstico'
            case 2: return fallbackBlockName || 'Parcial'
            case 3: return 'General'
            case 4: return 'Acreditación'
            case 5: return 'Certificación'
            default: return fallbackBlockName || 'Evaluación'
        }
    }

    const buildMetaRows = ({ header, metadata }) => {
        const { studentName, groupName, courseName } = header ?? {}
        const {
            evaluationType, finishDate, attemptNumber,
            totalQuestions, correctQuestions, incorrectQuestions, score, blockName
        } = metadata ?? {}

        return [
            ['Nombre:',                      studentName ?? '-'],
            ['Grupo:',                       groupName ?? '-'],
            ['Curso:',                       courseName ?? '-'],
            ['Evaluación:',                  getEvaluationTypeName(evaluationType, blockName)],
            ['Fecha en que se presentó:',    finishDate ? FormatDateTime(finishDate) : '-'],
            ['Número de oportunidad:',       attemptNumber ?? '-'],
            ['Total de preguntas:',          totalQuestions ?? '-'],
            ['Preguntas correctas:',         correctQuestions ?? '-'],
            ['Preguntas incorrectas:',       incorrectQuestions ?? '-'],
            ['Calificación:',                score ?? '-'],
            [],
            ['Detalle de evaluación:', '']
        ]
    }

    const buildDetailObjectRows = ({ evaluationType, details, finalAveragePercent }) => {
        const isPartial = Number(evaluationType) === 2
        const headerOrder  = ['blockName', 'itemName', 'result']
        const headerLabels = isPartial
            ? { blockName: 'Nombre del bloque', itemName: 'Tema', result: 'Aprobado/No aprobado' }
            : { blockName: 'Nombre del bloque', itemName: 'Tema', result: 'Resultado' }

        const safeDetails = Array.isArray(details) ? details : []
        const rows = safeDetails.map(d => ({
            blockName: d?.blockName ?? '-',
            itemName:  d?.itemName  ?? '-',
            result:    `${d?.percent ?? 0}%`
        }))

        rows.push({ blockName: '', itemName: '', result: '' })
        rows.push({ blockName: '', itemName: '', result: '' })
        rows.push({ blockName: 'Promedio', itemName: '', result: `${finalAveragePercent ?? 0}%` })

        return { headerOrder, headerLabels, rows }
    }

    const buildWorksheetForReport = ({ header, metadata, details, finalAveragePercent }) => {
        try {
            if (!metadata) throw new Error('metadata vacío')
            if (metadata?.evaluationType == null) throw new Error('evaluationType ausente en metadata')
            
            const metaRows = buildMetaRows({ header, metadata })
            const { headerOrder, headerLabels, rows } = buildDetailObjectRows({
                evaluationType: metadata.evaluationType,
                details: Array.isArray(details) ? details : [],
                finalAveragePercent
            })

            const colWidths = [32, 64, 16]
            return BuildSheetWithMeta({ metaRows, rows, headerOrder, headerLabels, colWidths })
        } catch (err) {
            console.error('buildWorksheetForReport:', err)
            const metaRows = [['Reporte', ''], ['Estado', `Error: ${err?.message ?? 'desconocido'}`]]
            return BuildSheetWithMeta({
                metaRows,
                rows: [{ note: 'Sin datos de detalle' }],
                headerOrder: ['note'],
                headerLabels: { note: 'Nota' },
                colWidths: [32]
            })
        }
    }


    // =========================================================================
    // 5. EFECTOS DE CARGA INICIAL
    // =========================================================================
    useEffect(() => {
        async function ValidateAndInit() {
            const tmpSession = await ValidateSession()
            if (tmpSession?.success) {
                // Validación para Líder (Ajustar if según tu UserStatus de líder)
                const user = tmpSession.data?.user
                const idCenter = user?.idCenter ?? user?.centerId ?? user?.center?.id ?? null
                const centerName = user?.centerName ?? user?.center?.name ?? ''
                setCenterInfo({ idCenter, centerName })
                
                await LoadInitialData()
            } else {
                navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
            }
        }
        ValidateAndInit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const LoadInitialData = async () => {
        try {
            setIsWorking(true)
            setErrorMessage(null)
            setIsInstructorsListLoading(true)

            // Cargar listado inicial de instructores
            const response = await GetInstructorsList()
            if (response.success) {
                setInstructorsList(response.data)
            } else {
                setErrorMessage(response?.message ?? 'No fue posible cargar los instructores.')
            }
        } catch (error) {
            await LogErrors('LoadInitialData - Results', error)
            setErrorMessage('Ocurrió un error al cargar la información inicial.')
        } finally {
            setIsWorking(false)
            setIsLoading(false)
            setIsInstructorsListLoading(false)
        }
    }


    // =========================================================================
    // 6. HANDLERS PARA CASCADA DE SELECTORES
    // =========================================================================
    
    /**
     * Paso 1: Seleccionar Instructor. Limpia todo hacia abajo y extrae los grupos localmente.
     */
    const handleSelectInstructor = (instructorId) => {
        setSelectedInstructorId(instructorId)
        
        // Reset cascada
        setSelectedGroupId(0)
        setSelectedCourseId(0)
        setSelectedEvaluationId(0)
        setGroupsList([])
        setCoursesList([])
        setEvaluationsList([])
        resetTableData()

        if (!instructorId) return

        // Extraer los grupos directamente del JSON del instructor para evitar otra llamada a API
        const selectedInstructor = instructorsList.find(inst => inst.idInstructor === instructorId)
        if (selectedInstructor && selectedInstructor.groups) {
            setGroupsList(selectedInstructor.groups)
        }
    }

    /**
     * Paso 2: Seleccionar Grupo. Solicita los Cursos pertenecientes a ese grupo e instructor.
     */
    const handleSelectGroup = async (groupId) => {
        try {
            setSelectedGroupId(groupId)
            
            // Reset cascada
            setSelectedCourseId(0)
            setSelectedEvaluationId(0)
            setCoursesList([])
            setEvaluationsList([])
            resetTableData()

            if (!groupId || !selectedInstructorId) return

            setIsCoursesListLoading(true)
            setIsWorking(true)
            setErrorMessage(null)

            const response = await GetCoursesForSelectedGroup(selectedInstructorId, groupId)
            if (response.success) {
                setCoursesList(response.data || [])
            } else {
                setErrorMessage(response?.message ?? 'No fue posible cargar los cursos.')
            }
        } catch (e) {
            await LogErrors('handleSelectGroup - Results', e)
            setErrorMessage('Error interno al cargar los cursos.')
        } finally {
            setIsWorking(false)
            setIsCoursesListLoading(false)
        }
    }

    /**
     * Paso 3: Seleccionar Curso. Solicita las Evaluaciones disponibles.
     */
    const handleSelectCourse = async (courseId) => {
        try {
            setSelectedCourseId(courseId)
            
            // Reset cascada
            setSelectedEvaluationId(0)
            setEvaluationsList([])
            resetTableData()

            if (!courseId) return

            setIsEvaluationsListLoading(true)
            setIsWorking(true)
            setErrorMessage(null)

            const response = await GetEvaluationsForSelectedCourse(courseId)
            if (response.success) {
                setEvaluationsList(response.data || [])
            } else {
                setErrorMessage(response?.message ?? 'No fue posible cargar las evaluaciones.')
            }
        } catch (e) {
            await LogErrors('handleSelectCourse - Results', e)
            setErrorMessage('Error interno al cargar las evaluaciones.')
        } finally {
            setIsWorking(false)
            setIsEvaluationsListLoading(false)
        }
    }

    /**
     * Paso 4: Seleccionar Evaluación. Prepara el terreno para el botón Filtrar.
     */
    const handleSelectEvaluation = (evaluationId) => {
        setSelectedEvaluationId(evaluationId)
        resetTableData()
    }

    const resetTableData = () => {
        setAllRows([])
        setFilteredRows([])
        setSearchText('')
        setPageNumber(1)
        setHasMorePages(false)
    }


    // =========================================================================
    // 7. FUNCIONES PARA POBLAR TABLA Y PAGINACIÓN
    // =========================================================================
    
    const FetchResultsFromApi = async (page = pageNumber, size = pageSize) => {
        try {
            setIsLoading(true)

            if (!selectedInstructorId || !selectedGroupId || !selectedCourseId || !selectedEvaluationId) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Completa todos los filtros para continuar.' })
                return
            }

            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo resultados solicitados...' })
            setIsWorking(true)
            setErrorMessage(null)

            const requestData = {
                idInstructor: selectedInstructorId,
                idGroup: selectedGroupId,
                idCourse: selectedCourseId,
                idBlock: selectedEvaluationId,
                pageNumber: page,
                pageSize: size,
                attemptScoreFilter: selectedAttemptScoreFilter
            }

            const response = await GetFilteredTableResults(requestData)

            if (response.success) {
                const payload = response.data ?? {}
                const rowsReturned = Array.isArray(payload) ? payload : (payload.rows ?? [])
                
                setAllRows(rowsReturned)
                setFilteredRows(rowsReturned)

                if (!Array.isArray(payload)) {
                    setPageNumber(payload.page ?? page)
                    setPageSize(payload.pageSize ?? size)
                    setHasMorePages(Boolean(payload.hasMore))
                }
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mostrando resultados' })
            } else {
                resetTableData()
                showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message ?? 'No se encontraron resultados.' })
            }
        } catch (error) {
            await LogErrors('FetchResultsFromApi - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error interno al obtener los resultados de la tabla.' })
        } finally {
            setIsWorking(false)
            setIsLoading(false)
        }
    }

    const gotoPrevPage = () => {
        const prev = Math.max(1, pageNumber - 1)
        if (prev !== pageNumber) FetchResultsFromApi(prev, pageSize)
    }
    const gotoNextPage = () => {
        if (hasMorePages) FetchResultsFromApi(pageNumber + 1, pageSize)
    }


    // =========================================================================
    // 8. FUNCIONES DE EXPORTACIÓN A EXCEL
    // =========================================================================

    /**
     * Exportar TODOS los registros sin aplicar filtros (Botón Global)
     */
    const handleExportGlobalResults = async () => {
        try {
            if (!selectedInstructorId) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona un instructor primero.' })
                return
            }

            setIsLoading(true)
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte completo. Esto puede demorar...' })
            
            const response = await GetAllResultsForInstructor(selectedInstructorId)

            if (response.success && response.data?.rows) {
                const rows = response.data.rows.map(e => ({
                    groupName: e.groupName,
                    courseName: e.courseName,
                    name: e.name,
                    lastName: e.lastName,
                    email: e.email,
                    user: e.user,
                    blockName: e.blockName,
                    attemptNumber: e.attemptNumber,
                    finishDate: FormatDateTime(e.finishDate),
                    score: e.score
                }))

                const headerOrder = ['groupName', 'courseName', 'name', 'lastName', 'email', 'user', 'blockName', 'attemptNumber', 'finishDate', 'score']
                const headerLabels = {
                    groupName: 'Nombre del grupo',
                    courseName: 'Curso',
                    name: 'Nombre(s)',
                    lastName: 'Apellidos',
                    email: 'Correo',
                    user: 'Usuario',
                    blockName: 'Tipo de evaluación',
                    attemptNumber: 'Oportunidad',
                    finishDate: 'Fecha',
                    score: 'Calificación'
                }
                const colWidths = [18, 16, 16, 16, 28, 16, 48, 12, 18, 14]

                const metaRows = [
                    ['Reporte:', 'Resultados de los Ztudiantes'],
                    ['Fecha de exportación:', new Date().toISOString().slice(0, 10)],
                    ['Total registros:', rows.length]
                ]

                const ws = BuildSheetWithMeta({ metaRows, rows, headerOrder, headerLabels, colWidths })
                const instNameFile = (response.data.instructorName || 'Instructor').replace(/[\\/:*?"<>|]/g,'_')
                
                BuildAndDownloadWorkbook(`Reporte_Completo_${instNameFile}_${new Date().toISOString().slice(0,10)}.xlsx`, [
                    { name: 'Resultados', ws }
                ])

                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay información disponible para exportar.' })
            }
        } catch (error) {
            await LogErrors('handleExportGlobalResults - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error fatal al exportar el documento.' })
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Exportar la tabla actualmente visible y filtrada
     */
    const handleExportFilteredTable = async () => {
        try {
            setIsLoading(true)

            if (!selectedInstructorId || !selectedGroupId || !selectedCourseId || !selectedEvaluationId) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Completa todos los filtros para continuar.' })
                return
            }

            // Si la tabla ya tiene TODO lo que arrojó el filtro (una sola página, sin más
            // resultados pendientes), exportamos directo lo que ya está cargado en pantalla,
            // sin volver a consultar el endpoint. Si hay más de una página, sí hace falta pedir
            // el set completo sin paginar (returnAll), porque la tabla solo tiene la página actual.
            const isSinglePage = pageNumber === 1 && !hasMorePages

            let allFilteredRows

            if (isSinglePage) {
                allFilteredRows = allRows
            } else {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo todos los resultados encontrados...' })

                const response = await GetFilteredTableResults({
                    idInstructor: selectedInstructorId,
                    idGroup: selectedGroupId,
                    idCourse: selectedCourseId,
                    idBlock: selectedEvaluationId,
                    pageNumber: 1,
                    pageSize: pageSize,
                    returnAll: true,
                    attemptScoreFilter: selectedAttemptScoreFilter
                })

                if (!response.success) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message ?? 'No ha sido posible obtener los resultados a exportar.' })
                    return
                }

                const payload = response.data ?? {}
                allFilteredRows = Array.isArray(payload) ? payload : (payload.rows ?? [])
            }

            if (!allFilteredRows.length) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay datos para exportar.' })
                return
            }

            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Exportando tabla...' })

            const instructorName = (instructorsList ?? []).find(i => i.idInstructor === selectedInstructorId)?.instructorName ?? '—'
            const groupName      = (groupsList ?? []).find(g => g.idGroup === selectedGroupId)?.groupName ?? '—'
            const selectedCourseObj = (coursesList ?? []).find(c => c.idCourse === selectedCourseId)
            const courseName     = selectedCourseObj
                ? `${selectedCourseObj.nameCourse}${selectedCourseObj.level ? ` (Nivel ${selectedCourseObj.level})` : ''}`
                : '—'
            const evaluationName = (evaluationsList ?? []).find(e => e.idBlock === selectedEvaluationId)?.name ?? '—'

            const rows = allFilteredRows.map(r => ({
                fullName: `${r.name ?? ''} ${r.lastName ?? ''}`.trim(),
                user: r.user,
                attemptNumber: r.attemptNumber,
                blockName: r.blockName ?? r.nameEvaluation ?? r.type ?? '-',
                finishDate: r.finishDate ? FormatDateTime(r.finishDate) : '-',
                score: r.score
            }))

            const headerOrder = ['fullName', 'user', 'attemptNumber', 'blockName', 'finishDate', 'score']
            const headerLabels = {
                fullName: 'Nombre',
                user: 'Usuario',
                attemptNumber: 'Oportunidad',
                blockName: 'Tipo de evaluación',
                finishDate: 'Fecha y hora de finalización',
                score: 'Calificación'
            }
            const colWidths = [24, 16, 12, 38, 22, 14]

            const metaRows = [
                ['Instructor:',   instructorName],
                ['Grupo:',        groupName],
                ['Curso:',        courseName],
                ['Evaluación:',   evaluationName],
                ['Tipo de resultados:', ATTEMPT_SCORE_FILTER_LABELS[selectedAttemptScoreFilter]],
                ['Total registros encontrados:', rows.length]
            ]

            const ws = BuildSheetWithMeta({ metaRows, rows, headerOrder, headerLabels, colWidths })

            BuildAndDownloadWorkbook(`Resultados_Desglosados_${new Date().toISOString().slice(0,10)}.xlsx`, [
                { name: 'Resultados', ws }
            ])

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
        } catch (error) {
            await LogErrors('handleExportFilteredTable - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible exportar la tabla.' })
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Exportar reporte super detallado de 1 solo registro
     */
    const handleExportSingleDetailReport = async (row) => {
        try {
            setIsLoading(true)
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo detalle profundo del estudiante...' })

            const groupNameFromState = (groupsList ?? []).find(g => g.idGroup === (row.idGroup ?? selectedGroupId))?.groupName ?? row.groupName ?? ''
            const selectedCourseObjDetail = (coursesList ?? []).find(c => c.idCourse === (row.idCourse ?? selectedCourseId))
            const courseNameFromState = selectedCourseObjDetail
                ? `${selectedCourseObjDetail.nameCourse}${selectedCourseObjDetail.level ? ` (Nivel ${selectedCourseObjDetail.level})` : ''}`
                : (row.courseName ?? '')

            const payloadDetails = {
                studentId: row.idZtudiante ?? row.studentId ?? row.id ?? null,
                courseId: row.idCourse ?? selectedCourseId ?? null,
                evaluationToken: row.token,
                groupId: row.idGroup ?? selectedGroupId ?? null,
                groupName: groupNameFromState,
                attemptNumber: row.attemptNumber ?? 1
            }

            const response = await GetDetailedSingleReport(payloadDetails)
            
            if (!response?.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message ?? 'No fue posible obtener el detalle.' })
                return
            }

            const data = response.data ?? {}
            const { header, metadata, details, finalAveragePercent } = data

            const enrichedHeader = {
                ...header,
                groupName: header?.groupName || groupNameFromState,
                courseName: courseNameFromState || header?.courseName
            }

            const ws = buildWorksheetForReport({
                header: enrichedHeader,
                metadata,
                details,
                finalAveragePercent
            })

            const studentForFile = (enrichedHeader?.studentName || 'Alumno').replace(/[\\/:*?"<>|]/g,'_')
            const when = metadata?.finishDate ? FormatDateTime(metadata.finishDate).replace(/[\\/:*?"<>|]/g,'_') : new Date().toISOString().slice(0,10)
            const evalName = getEvaluationTypeName(metadata?.evaluationType, metadata?.blockName).replace(/[\\/:*?"<>|]/g,'_')

            BuildAndDownloadWorkbook(`Reporte_Detalle_${studentForFile}_${evalName}_${when}.xlsx`, [
                { name: 'Resultados', ws }
            ])

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte generado.' })
        } catch (error) {
            await LogErrors('handleExportSingleDetailReport - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error al generar el reporte individual.' })
        } finally {
            setIsLoading(false)
        }
    }


    // =========================================================================
    // 8.1 VER DETALLE PREGUNTA POR PREGUNTA DE UNA FILA
    // =========================================================================
    const HandleShowQuestionDetail = async (row) => {
        try {
            setIsFetchingDetail(true)
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo detalle de la evaluación...' })

            const payload = {
                studentId: row.idZtudiante ?? row.studentId ?? row.id ?? null,
                courseId: row.idCourse ?? selectedCourseId ?? null,
                evaluationToken: row.token
            }

            const resp = await BLGetQuestionDetail(payload)

            if (!resp?.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: resp?.message ?? 'No fue posible obtener el detalle de la evaluación.' })
                return
            }

            const selectedCourseObjDetail = (coursesList ?? []).find(c => c.idCourse === (row.idCourse ?? selectedCourseId))
            const courseNameWithLevel = selectedCourseObjDetail
                ? `${selectedCourseObjDetail.nameCourse}${selectedCourseObjDetail.level ? ` (Nivel ${selectedCourseObjDetail.level})` : ''}`
                : resp.data?.courseName

            setQuestionDetail({ ...resp.data, courseName: courseNameWithLevel })
            setActiveDetailFilter('all')
            setShowQuestionDetailPage(true)
        } catch (error) {
            await LogErrors('HandleShowQuestionDetail - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error al obtener el detalle de la evaluación.' })
        } finally {
            setIsFetchingDetail(false)
        }
    }

    const HandleBackToTable = () => {
        setShowQuestionDetailPage(false)
        setQuestionDetail(null)
    }

    // El enunciado y el análisis llegan con etiquetas HTML (para pintarse con
    // dangerouslySetInnerHTML); en un Excel solo interesa el texto.
    const StripHtml = (html) => String(html ?? '').replace(/<[^>]*>/g, '').trim()

    const ExportQuestionDetail = async () => {
        try {
            const questions = questionDetail?.questions ?? []

            if (!questions.length) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay preguntas disponibles para exportar.' })
                return
            }

            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte...' })

            // Se exportan TODAS las preguntas, sin importar el filtro (Todas/Correctas/Incorrectas)
            // activo en pantalla en ese momento. El número de pregunta solo se repite en la primera
            // tarea de cada pregunta, para no saturar la columna cuando hay varias tareas.
            const rows = questions.flatMap((question, qIndex) =>
                (question.tasks ?? []).map((task, taskIndex) => ({
                    questionNumber: taskIndex === 0 ? qIndex + 1 : '',
                    taskNumber: taskIndex + 1,
                    instruction: StripHtml(task.instruction),
                    result: task.result === 1 ? 'Correcto' : 'Incorrecto',
                    analysis: task.result === 0 ? StripHtml(task.analysis) : ''
                }))
            )

            const headerOrder = ['questionNumber', 'taskNumber', 'instruction', 'result', 'analysis']
            const headerLabels = {
                questionNumber: 'Pregunta',
                taskNumber: 'Tarea',
                instruction: 'Instrucciones',
                result: 'Resultado',
                analysis: 'Análisis'
            }
            const colWidths = [10, 8, 70, 14, 60]

            const metaRows = [
                ['Estudiante:', questionDetail.studentName ?? '-'],
                ['Curso:', questionDetail.courseName ?? '-'],
                ['Evaluación:', questionDetail.blockName ?? '-'],
                ['Fecha:', questionDetail.finishDate ? FormatDateTime(questionDetail.finishDate) : '-'],
                ['Resultado:', questionDetail.score ?? '-'],
                ['Total de preguntas:', questionDetail.totalQuestions ?? '-'],
                ['Preguntas correctas:', questionDetail.correctQuestions ?? '-'],
                ['Preguntas incorrectas:', questionDetail.incorrectQuestions ?? '-'],
                ['Preguntas no contestadas:', questionDetail.unansweredQuestions ?? 0]
            ]

            const ws = BuildSheetWithMeta({ metaRows, rows, headerOrder, headerLabels, colWidths })

            const safeStudentName = (questionDetail.studentName || 'Estudiante').replace(/[\\/:*?"<>|]/g, '_')

            BuildAndDownloadWorkbook(`Detalle_${safeStudentName}_${new Date().toISOString().slice(0, 10)}.xlsx`, [
                { name: 'Detalle de la evaluación', ws }
            ])

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte generado.' })
        } catch (error) {
            await LogErrors('ExportQuestionDetail - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error al exportar el detalle de la evaluación.' })
        }
    }

    // Replica renderQuestions() de /results/items, pero sin el botón "Ir al tema": un
    // Líder no puede activar la sesión de repaso del propio estudiante.
    const renderLeaderQuestions = () => {
        const filteredQuestions = (questionDetail?.questions ?? [])
            .map((question, originalIndex) => {
                const tasksFiltered = question?.tasks?.filter((task) => {
                    if (activeDetailFilter === 'correct') return task.result === 1
                    if (activeDetailFilter === 'incorrect') return task.result === 0
                    return true
                })

                if (!tasksFiltered || tasksFiltered.length === 0) return null

                return { ...question, tasks: tasksFiltered, originalIndex }
            })
            .filter(Boolean)

        if (filteredQuestions.length === 0) {
            return <p className='Result__Text'>Sin resultados.</p>
        }

        return filteredQuestions.map((filteredQuestion, i) => (
            isDetailAccreditation ? (
                <div key={i} className='Result__Accreditation'>
                    <p className='Result__Accreditation__Title'>{`Pregunta ${filteredQuestion.originalIndex + 1}`}</p>
                    {filteredQuestion.tasks.map((task, taskIndex) => (
                        <div key={taskIndex} className={`Result__Question ${task.result === 0 ? 'Result__Question--Bad' : 'Result__Question--Good'}`}>
                            <div className='Result__Question__Detail'>
                                <p dangerouslySetInnerHTML={{ __html: task.instruction }} />
                                {task.result === 0 ? (
                                    <p className='Result__Question__Detail--Bad'>{task.analysis}</p>
                                ) : (
                                    <p className='Result__Question__Detail--Good'>¡Correcto!</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div key={i} className={`Result__Question ${filteredQuestion.tasks[0].result === 0 ? 'Result__Question--Bad' : 'Result__Question--Good'}`}>
                    <div className='Result__Question__Detail'>
                        <p dangerouslySetInnerHTML={{ __html: filteredQuestion.tasks[0].instruction }} />
                        {filteredQuestion.tasks[0].result === 0 ? (
                            <p className='Result__Question__Detail--Bad'>{filteredQuestion.tasks[0].analysis}</p>
                        ) : (
                            <p className='Result__Question__Detail--Good'>¡Correcto!</p>
                        )}
                    </div>
                </div>
            )
        ))
    }


    // =========================================================================
    // 9. RENDER DE LA VISTA
    // =========================================================================
    
    return isLoading ? (
        <Loading />
    ) : showQuestionDetailPage && questionDetail ? (
        <InstructorBase urlToGo={null} idHelp={HELP_TYPE.LEADER.REPORTS} page={WEBPAGES.LEADER.REPORTS} role={ROLES.LEADER}>
            <main className='container Result Container-FullHeight'>
                <button type='button' className='Button-Return' onClick={HandleBackToTable}><i className='bi bi-caret-left-fill'></i></button>

                <div>
                    <div className='Result__Box Tables--Shadow'>
                        <p className='Result__Box__Title'>Resultado de: {questionDetail.studentName}</p>
                        <p className='Result__Box__Info'>{`Curso: ${questionDetail.courseName}`}</p>
                        <p className='Result__Box__Info'>{`Evaluación: ${questionDetail.blockName}`}</p>
                        <p className='Result__Box__Info'>{`Resultado: ${questionDetail.score}`}</p>
                        <p className='Result__Box__Info'>{`Total de preguntas: ${questionDetail.totalQuestions}`}</p>
                        {isDetailAccreditation ? (
                            <>
                                <p className='Result__Box__Info'>{`Total de tareas solicitadas: ${totalDetailTasks}`}</p>
                                <p className='Result__Box__Info'>{`Tareas correctas: ${correctDetailTasks}`}</p>
                                <p className='Result__Box__Info Last-Child'>{`Tareas incorrectas: ${incorrectDetailTasks}`}</p>
                            </>
                        ) : (
                            <>
                                <p className='Result__Box__Info'>{`Preguntas correctas: ${questionDetail.correctQuestions}`}</p>
                                <p className='Result__Box__Info'>{`Preguntas incorrectas: ${questionDetail.incorrectQuestions}`}</p>
                                <p className='Result__Box__Info Last-Child'>{`Preguntas no contestadas: ${questionDetail.unansweredQuestions ?? 0}`}</p>
                            </>
                        )}
                    </div>

                    <button
                        type='button'
                        className='Btn__Ztudia--PrimaryOutline m-auto mt-4'
                        onClick={ExportQuestionDetail}
                    >
                        <i className='bi bi-filetype-xlsx'></i> Exportar
                    </button>

                    <p className='Result__Title Result__Divisor'>Preguntas del examen</p>

                    <div className='Result__Filter'>
                        <button type='button' className={`Result__Filter__Buttons ${activeDetailFilter === 'all' ? 'Result__Filter__Buttons--Active' : ''}`} onClick={() => setActiveDetailFilter('all')}>
                            Todas ({isDetailAccreditation ? totalDetailTasks : questionDetail.totalQuestions})
                        </button>
                        <button type='button' className={`Result__Filter__Buttons ${activeDetailFilter === 'correct' ? 'Result__Filter__Buttons--Active' : ''}`} onClick={() => setActiveDetailFilter('correct')}>
                            Correctas ({isDetailAccreditation ? correctDetailTasks : questionDetail.correctQuestions})
                        </button>
                        <button type='button' className={`Result__Filter__Buttons ${activeDetailFilter === 'incorrect' ? 'Result__Filter__Buttons--Active' : ''}`} onClick={() => setActiveDetailFilter('incorrect')}>
                            Incorrectas ({isDetailAccreditation ? incorrectDetailTasks : questionDetail.incorrectQuestions})
                        </button>
                    </div>

                    {renderLeaderQuestions()}
                </div>
            </main>
        </InstructorBase>
    ) : (
        <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.LEADER.REPORTS} page={WEBPAGES.LEADER.REPORTS} role={ROLES.LEADER}>
            <section className='container Instructor__Reports'>
                <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>Resultados de los Ztudiantes</p>

                {/* 1. SELECCIÓN DE INSTRUCTOR PARA HABILITAR EL REPORTE GENERAL */}
                <div className='Instructor__Results__Reports__Filters Instructor__SpaceUp' style={{ justifyContent: 'center' }}>
                    {isInstructorsListLoading ? (
                        <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                            <option>Cargando instructores...</option>
                        </select>
                    ) : (
                        <select
                            className='Instructor__Results__Reports__Filters__List'
                            value={selectedInstructorId}
                            onChange={(e) => handleSelectInstructor(parseInt(e.target.value, 10))}
                            disabled={isWorking || instructorsList.length === 0}
                        >
                            <option value={0}>1. Selecciona un instructor</option>
                            {instructorsList.map(inst => (
                                <option key={inst.idInstructor} value={inst.idInstructor}>{inst.instructorName}</option>
                            ))}
                        </select>
                    )}
                    
                    <button 
                        type='button' 
                        className='Btn__Ztudia--Primary m-auto m-md-0' 
                        onClick={handleExportGlobalResults}
                        disabled={selectedInstructorId === 0 || isWorking}
                    >
                        <i className='bi bi-filetype-xlsx'></i> Exportar todo
                    </button>
                </div>

                <p className='Instructor__Text--Center Instructor__SpaceUp'>----- O -----</p>

                {/* 2. CASCADA DE FILTROS: GRUPO -> CURSO -> EVALUACIÓN */}
                <div className='Instructor__Results__Reports__Filters'>
                    
                    {/* GRUPO */}
                    <select
                        className='Instructor__Results__Reports__Filters__List'
                        value={selectedGroupId}
                        onChange={(e) => handleSelectGroup(parseInt(e.target.value, 10))}
                        disabled={isWorking || selectedInstructorId === 0 || groupsList.length === 0}
                    >
                        <option value={0}>2. Selecciona un grupo</option>
                        {groupsList.map(g => (
                            <option key={g.idGroup} value={g.idGroup}>{g.groupName}</option>
                        ))}
                    </select>

                    {/* CURSO */}
                    {isCoursesListLoading ? (
                        <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                            <option>Cargando cursos...</option>
                        </select>
                    ) : (
                        <select
                            className='Instructor__Results__Reports__Filters__List'
                            value={selectedCourseId}
                            onChange={(e) => handleSelectCourse(parseInt(e.target.value, 10))}
                            disabled={isWorking || selectedGroupId === 0 || coursesList.length === 0}
                        >
                            <option value={0}>3. Selecciona un curso</option>
                            {coursesList.map(c => (
                                <option key={c.idCourse} value={c.idCourse}>{c.nameCourse}{c.level && ` (Nivel ${c.level})`}</option>
                            ))}
                        </select>
                    )}

                    {/* EVALUACIÓN */}
                    {isEvaluationsListLoading ? (
                        <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                            <option>Cargando evaluaciones...</option>
                        </select>
                    ) : (
                        <select
                            className='Instructor__Results__Reports__Filters__List'
                            value={selectedEvaluationId}
                            onChange={(e) => handleSelectEvaluation(parseInt(e.target.value, 10))}
                            disabled={isWorking || selectedCourseId === 0 || evaluationsList.length === 0}
                        >
                            <option value={0}>4. Selecciona evaluación</option>
                            {evaluationsList.map(ev => (
                                <option key={ev.idBlock} value={ev.idBlock}>{ev.name}</option>
                            ))}
                        </select>
                    )}

                    {/* TIPO DE RESULTADOS */}
                    <select
                        className='Instructor__Results__Reports__Filters__List'
                        value={selectedAttemptScoreFilter}
                        onChange={(e) => setSelectedAttemptScoreFilter(parseInt(e.target.value, 10))}
                        disabled={isWorking || selectedEvaluationId === 0}
                    >
                        {Object.values(ATTEMPT_SCORE_FILTER).map(value => (
                            <option key={value} value={value}>{ATTEMPT_SCORE_FILTER_LABELS[value]}</option>
                        ))}
                    </select>

                    {/* APLICAR FILTRO A LA TABLA */}
                    <button
                        type='button'
                        className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__Filters__Button'
                        onClick={() => FetchResultsFromApi(1, pageSize)}
                        disabled={isWorking || !selectedInstructorId || !selectedGroupId || !selectedCourseId || !selectedEvaluationId}
                    >
                        <i className='bi bi-funnel'></i> Aplicar filtro
                    </button>
                </div>

                {/* 3. CONTROLES DE LA TABLA Y EXPORTACIÓN ACTUAL */}
                <div className='Instructor__SpaceUp'>
                    <Search onChange={setSearchText} extraClassName={'Instructor__Results--V2__Search'} />
                </div>

                <button
                    type='button'
                    className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__ExportButton'
                    onClick={handleExportFilteredTable}
                    disabled={!allRows?.length}
                >
                    <i className='bi bi-filetype-xlsx'></i> Exportar
                </button>

                {/* 4. LA TABLA */}
                <section className='Tables__Container Instructor__Results--V2__Reports__ContainerTable Tables--Shadow Tables--Background Instructor__SpaceUp'>
                    <table className='Tables__Table Instructor__Results--V2__Reports__ContainerTable__Table'>
                        <thead>
                            <tr className='Tables__Table__Row--Header'>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>Nombre</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>Usuario</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>Tipo de evaluación</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>Oport.</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>Fecha y hora de finalización</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>Calif.</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__ActionColumn'></td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__ActionColumn'></td>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows?.length ? (
                                filteredRows.map((r, idx) => (
                                    <tr key={r.id ?? idx} className='Tables__Table__Row'>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>
                                            {`${r.name ?? ''} ${r.lastName ?? ''}`.trim()}
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>{r.user}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>
                                            {r.blockName ?? r.nameEvaluation ?? r.type ?? '-'}
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>{r.attemptNumber}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>
                                            {r.finishDate ? `${FormatDateTime(r.finishDate)} hrs.` : '-'}
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__TextColumn'>{r.score ?? '-'}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__ActionColumn'>
                                            <button
                                                type='button'
                                                className='Btn__Ztudia--PrimaryOutline Instructor__Results--V2__Reports__ContainerTable__Table__ActionColumn__Button'
                                                onClick={() => HandleShowQuestionDetail(r)}
                                                disabled={isFetchingDetail}
                                            >
                                                Detalles
                                            </button>
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results--V2__Reports__ContainerTable__Table__ActionColumn'>
                                            <button
                                                type='button'
                                                className='Btn__Ztudia--PrimaryOutline Instructor__Results--V2__Reports__ContainerTable__Table__ActionColumn__Button'
                                                onClick={() => handleExportSingleDetailReport(r)}
                                            >
                                                <i className='bi bi-filetype-xlsx'></i> Exportar
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr className='Tables__Table__Row'>
                                    <td className='Tables__Table__Row__Cell text-center py-4' colSpan={8}>
                                        {isWorking ? 'Consultando resultados en el servidor...' : 'Sin resultados por mostrar. Aplica los filtros superiores.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>

                {/* 5. PAGINACIÓN */}
                <div className='Instructor__Reports__Pagination'>
                    <p className='Instructor__Reports__Pagination__Info text-center mt-3'>Total de registros en esta página: {filteredRows.length}</p>
                    <p className='Instructor__Reports__Pagination__Info text-center mt-3'>Página {pageNumber}</p>

                    <div className='d-flex justify-content-center'>
                        {pageNumber > 1 && (
                            <button
                                type='button'
                                className='Btn__Ztudia--PrimaryOutline me-2'
                                disabled={pageNumber <= 1 || isWorking}
                                onClick={gotoPrevPage}
                            >
                                <i className="bi bi-caret-left-fill"></i> Anterior
                            </button>
                        )}

                        {hasMorePages && (
                            <button
                                type='button'
                                className='Btn__Ztudia--PrimaryOutline ms-2'
                                disabled={!hasMorePages || isWorking}
                                onClick={gotoNextPage}
                            >
                                Siguiente <i className="bi bi-caret-right-fill"></i>
                            </button>
                        )}
                    </div>
                </div>

                {errorMessage && errorMessage !== '' && (<p className='Instructor__Message--Error text-center mt-3'>{errorMessage}</p>)}
            </section>
        </InstructorBase>
    )
}

export { Reports };